/**
 * Jovii — WHICH SHELL IS ON SCREEN.
 *
 * ONE BREAKPOINT, WRITTEN DOWN ONCE: **768px**.
 *
 *   < 768px   the phone shell — the logo and a scrolling row of glass bubbles at
 *             the top (they leave with the content), a fixed tab bar at the bottom.
 *   ≥ 768px   the three-zone top bar — avatar and its menu on the left, the
 *             primary navigation in the middle, the logo on the right.
 *
 * 768 and not 700 or 820 because the smallest tablet this ships to is 820×1180 in
 * portrait, and the largest phone is 430 across. Anything between the two is a
 * desktop window somebody has made narrow, and a top bar is the right answer there.
 * A phone in landscape is 844×390: it gets the TOP bar, deliberately — width is
 * what it has and height is what it has not.
 *
 * The same value lives in CSS as a plain `@media (min-width: 768px)`. It is not a
 * custom property because a media query cannot read one; if you change it here,
 * change every `768` in `web/src/lib/ui/` and `web/src/app.css` with it. There is a
 * test for that in `shell.test.ts`.
 */

/** The one breakpoint. Below this the phone shell, at or above it the top bar. */
export const SHELL_BREAKPOINT = 768;

const QUERY = `(min-width: ${SHELL_BREAKPOINT}px)`;

/**
 * Live shell state. Read `shell.phone` in a component and it stays reactive.
 *
 * Seeded synchronously from the current viewport so the first paint is already
 * the right shell — a phone must never flash a desktop bar and then re-flow.
 */
export const shell: { phone: boolean } = $state({
  phone: typeof window === 'undefined' ? false : !window.matchMedia(QUERY).matches
});

let watching = false;

/**
 * Start following the viewport. Called once from `App.svelte`; safe to call again.
 * Returns a teardown, so an `$effect` can own it.
 */
export function watchShell(): () => void {
  if (watching || typeof window === 'undefined') return () => {};
  watching = true;

  const media = window.matchMedia(QUERY);
  const apply = (): void => {
    shell.phone = !media.matches;
  };
  apply();

  // Safari before 14 has only the deprecated form; both are cheap to attach.
  if (typeof media.addEventListener === 'function') media.addEventListener('change', apply);
  else media.addListener(apply);

  return () => {
    watching = false;
    if (typeof media.removeEventListener === 'function') media.removeEventListener('change', apply);
    else media.removeListener(apply);
  };
}
